import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/stores/auth.store';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add JWT token to headers
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle token refresh and errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { refreshToken, updateTokens, logout } = useAuthStore.getState();

      if (!refreshToken) {
        logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh token (use separate axios instance to avoid interceptor loop)
        const response = await axios.post(
          `${BASE_URL}/auth/refresh`,
          { refreshToken }
        );

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          response.data;

        updateTokens(newAccessToken, newRefreshToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // Generic HTTP methods
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((res) => res.data),

  // Auth methods
  login: (email: string, password: string) =>
    apiService.post('/auth/login', { email, password }),

  register: (name: string, email: string, password: string) =>
    apiService.post('/auth/register', { name, email, password }),

  refreshToken: (refreshToken: string) =>
    apiService.post('/auth/refresh', { refreshToken }),

  // Order methods
  getOrders: (status: 'active' | 'past', page: number = 1, limit: number = 20) =>
    apiService.get(`/orders?status=${status}&page=${page}&limit=${limit}`),

  getOrder: (orderId: string) =>
    apiService.get(`/orders/${orderId}`),

  cancelOrder: (orderId: string) =>
    apiService.patch(`/orders/${orderId}/cancel`, {}),

  // Payment methods
  createPayment: (paymentData: {
    orderId: string;
    paymentMethod: string;
    amount: number;
    successRedirectUrl: string;
    failureRedirectUrl: string;
  }) =>
    apiService.post('/payments/create', paymentData),

  verifyPayment: (paymentId: string) =>
    apiService.get(`/payments/${paymentId}/verify`),

  // Merchant pickup validation methods (for merchant dashboard)
  validatePickup: (orderReference: string) =>
    apiService.post('/orders/validate-pickup', { orderReference }),

  completeOrder: (orderId: string) =>
    apiService.patch(`/orders/${orderId}/complete`, {}),
};

export default apiService;
